import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Download, Paperclip } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DOCUMENT_TYPES, formatDateTime } from './constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AcademicDocumentsPanel = ({ athlete, profile, canEdit }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState({ open: false, document_type: 'School Report', title: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const fetchItems = async () => {
    if (!athlete?.id || !profile?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/academic/documents?athlete_id=${athlete.id}&academic_profile_id=${profile.id}`);
      setItems(r.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [athlete?.id, profile?.id]);

  const onPick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error('File too large (max 20 MB)'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('athlete_id', athlete.id);
      fd.append('academic_profile_id', profile.id);
      fd.append('document_type', dlg.document_type);
      if (dlg.title) fd.append('title', dlg.title);
      if (dlg.notes) fd.append('notes', dlg.notes);
      await axios.post(`${API}/academic/documents/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setDlg({ open: false, document_type: 'School Report', title: '', notes: '' });
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/academic/documents/${d.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = d.original_filename || d.title || 'document';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const remove = async (d) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/academic/documents/${d.id}`);
      toast.success('Document deleted');
      fetchItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-2" data-testid="academic-documents-list">
      <div className="flex justify-end">
        {canEdit && (
          <Button size="sm" onClick={() => setDlg(s => ({ ...s, open: true }))} data-testid="add-document-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Upload Document
          </Button>
        )}
      </div>
      {loading ? (
        <p className="text-sm text-gray-500 text-center py-6">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">No documents yet.</p>
      ) : items.map(d => (
        <div key={d.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800 flex items-center justify-between gap-3" data-testid={`academic-document-${d.id}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-0">{d.document_type}</Badge>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{d.title || d.original_filename}</p>
              </div>
              <p className="text-[10px] text-gray-400 mt-0.5">{formatDateTime(d.uploaded_at)}{d.original_filename && d.title ? ` · ${d.original_filename}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => download(d)} data-testid={`download-document-${d.id}`}>
              <Download className="w-3.5 h-3.5" />
            </Button>
            {canEdit && (
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(d)} data-testid={`delete-document-${d.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      ))}

      {/* Upload dialog */}
      <Dialog open={dlg.open} onOpenChange={(o) => setDlg(s => ({ ...s, open: o }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={dlg.document_type} onValueChange={v => setDlg(s => ({ ...s, document_type: v }))}>
                <SelectTrigger data-testid="document-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DOCUMENT_TYPES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title (optional)</Label>
              <Input value={dlg.title} onChange={e => setDlg(s => ({ ...s, title: e.target.value }))} placeholder="Defaults to the filename" data-testid="document-title-input" />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={3} value={dlg.notes} onChange={e => setDlg(s => ({ ...s, notes: e.target.value }))} />
            </div>
            <input ref={inputRef} type="file" className="hidden" onChange={onFile} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv" data-testid="document-file-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(s => ({ ...s, open: false }))} disabled={busy}>Cancel</Button>
            <Button onClick={onPick} disabled={busy} data-testid="document-pick-file-btn">{busy ? 'Uploading…' : 'Choose file & upload'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcademicDocumentsPanel;
